import { useTheme } from '@emotion/react';
import { useAtomValue } from 'jotai';
import React from 'react';

import { Alert, Drawer } from '~/components/blocks';
import { PatientProfileFooterAppointmentFragment } from '~/graphql/generated/graphql';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';

import { BookedButtons } from './BookedButtons';
import { ChargedButtons } from './ChargedButtons';
import { PendingButtons } from './PendingButtons';
import { SameDayDeliveryButtons } from './SameDayDeliveryButtons';
import { useFetchAppointment } from './use-fetch-appointment';
import { WaitingForChargeButtons } from './WaitingForChargeButtons';

type Props = {
  appointmentId: string;
  patientId: string;
};

export const Footer = React.memo((props: Props) => {
  const theme = useTheme();

  const appointment = useFetchAppointment(props.appointmentId);
  const isSentCongestionNotification = !!appointment?.congestionNotificationSentAt;

  const state = useAtomValue(profileDrawerState);

  const appointmentStatus = appointment?.status;
  const isVisible = (
    ['pending', 'booked', 'waiting_for_charge', 'charged', 'finished'] as Array<
      PatientProfileFooterAppointmentFragment['status'] | undefined
    >
  ).includes(appointmentStatus);

  if (!appointment || !isVisible) return null;

  if (appointmentStatus === 'finished') return null;

  const deliveryMethod = appointment.deliveryMethod;
  // 配達員を待っており、依頼から10分以内の場合はボタンを表示しない
  if (
    appointment.status === 'waiting_for_charge' &&
    deliveryMethod === 'same_day_delivery' &&
    appointment.uberDelivery?.status === 'pickup' &&
    !appointment.uberDelivery.isTimeElapsedNotification
  ) {
    return null;
  }

  // 配達よりも先に会計を終わらせてしまった時は、自動で完了になるので完了ボタンを出さないようにする
  if (
    appointment.status === 'charged' &&
    deliveryMethod === 'same_day_delivery' &&
    (appointment.uberDelivery?.status === 'pickup' ||
      appointment.uberDelivery?.status === 'dropoff' ||
      appointment.uberDelivery?.status === 'delivered')
  ) {
    return null;
  }

  return (
    <Drawer.Footer>
      {state.error && (
        <Alert status="error" marginBottom={theme.space.l}>
          {state.error}
        </Alert>
      )}
      {appointmentStatus === 'pending' ? (
        <PendingButtons appointmentId={appointment.id} patientId={props.patientId} />
      ) : appointmentStatus === 'booked' ? (
        <BookedButtons
          isSentCongestionNotification={isSentCongestionNotification}
          appointmentId={appointment.id}
          draftAppointmentId={appointment.draftAppointment?.id}
          patientId={props.patientId}
          telemedicine={appointment.telemedicine}
          immediate={appointment.immediate}
          encounter={appointment.encounter}
        />
      ) : deliveryMethod === 'same_day_delivery' ? (
        <SameDayDeliveryButtons appointment={appointment} />
      ) : appointmentStatus === 'waiting_for_charge' ? (
        <WaitingForChargeButtons
          isSentCongestionNotification={isSentCongestionNotification}
          appointmentId={appointment.id}
          telemedicine={appointment.telemedicine}
          draftAppointmentId={appointment.draftAppointment?.id}
        />
      ) : appointmentStatus === 'charged' ? (
        <ChargedButtons appointmentId={appointment.id} />
      ) : null}
    </Drawer.Footer>
  );
});

Footer.displayName = 'Footer';
